import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import { IUser } from '../../common/interfaces/User.interface';
import { DashboardUserRole } from '../../common/enums/DashboardUserRole';
import { createBaseQuery } from './utils/createBaseQuery';

export const userApi = createApi({
  reducerPath: 'UserApi',
  baseQuery: createBaseQuery('/dashboard-users'),
  tagTypes: ['User'],
  endpoints: (builder) => ({
    fetchUser: builder.query<
      { code: HttpStatus; data: IUser[] },
      { page: number; size: number }
    >({
      query: ({ page, size }) => `?page=${page}&size=${size}`,
      providesTags: (result) =>
        result
          ? [
              { type: 'User' as const, id: 'LIST' },
              ...result.data.map(({ id }) => ({
                type: 'User' as const,
                id,
              })),
            ]
          : [{ type: 'User' as const, id: 'LIST' }],
    }),
    addUser: builder.mutation<
      string,
      { name: string; email: string; role: DashboardUserRole }
    >({
      query: (newContent) => ({
        url: '',
        method: 'POST',
        body: newContent,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    updateUserStatus: builder.mutation<IUser, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: status === 'ACTIVE' ? `/${id}/activate` : `/${id}/deactivate`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    updateUserRole: builder.mutation<
      string,
      { id: string; role: DashboardUserRole }
    >({
      query: ({ id, role }) => ({
        url: `/${id}/role`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
  }),
});

export const {
  useFetchUserQuery,
  useAddUserMutation,
  useUpdateUserStatusMutation,
  useUpdateUserRoleMutation,
} = userApi;
